import React, { useState } from "react";
import Sidebar from "./Sidebar";
import "./manageClasses.css"; // Using the same professional CSS
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faPlus,
  faFilter,
  faSortAmountDown,
  faBookOpen,
  faCalculator,
  faCreditCard,
  faChartLine,
  faArrowRight,
  faCogs,
  faMoneyCheckAlt,
  faFileInvoiceDollar,
  faArrowTrendUp
} from "@fortawesome/free-solid-svg-icons";

const SetupAccounts = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");

  // Setup stats with icons
  const setupStats = [
    {
      icon: faCalculator,
      value: "14",
      label: "Classes Pending Setup",
      color: "var(--mc-accent-orange)"
    },
    {
      icon: faCreditCard,
      value: "8",
      label: "Fee Structures Defined",
      color: "var(--mc-accent-green)"
    },
    {
      icon: faChartLine,
      value: "6",
      label: "Active Revenue Streams",
      color: "var(--mc-accent-blue)"
    },
    {
      icon: faFileInvoiceDollar,
      value: "92.5%",
      label: "Collection Efficiency",
      color: "var(--mc-accent-indigo)"
    }
  ];

  // Class names
  const classes = [
    "Play Group",
    "Nursery",
    "Prep",
    "Class 1",
    "Class 2",
    "Class 3",
    "Class 4",
    "Class 5",
    "Class 6",
    "Class 7",
    "Class 8",
    "Class Pre 9",
    "Class 9",
    "Class 10",
  ];

  // Corresponding image filenames
  const imageFilenames = [
    "pg.png",
    "nur.png",
    "prep.png",
    "1.png",
    "2.png",
    "3.png",
    "4.png",
    "5.png",
    "6.png",
    "7.png",
    "8.png",
    "8.png",
    "9.png",
    "10.png",
  ];

  // Setup status for each class (mock data)
  const setupStatus = [
    { status: "pending", label: "Setup Required" },
    { status: "partial", label: "Partially Setup" },
    { status: "complete", label: "Fully Configured" },
    { status: "pending", label: "Setup Required" },
    { status: "complete", label: "Fully Configured" },
    { status: "pending", label: "Setup Required" },
    { status: "complete", label: "Fully Configured" },
    { status: "partial", label: "Partially Setup" },
    { status: "pending", label: "Setup Required" },
    { status: "complete", label: "Fully Configured" },
    { status: "pending", label: "Setup Required" },
    { status: "partial", label: "Partially Setup" },
    { status: "complete", label: "Fully Configured" },
    { status: "pending", label: "Setup Required" }
  ];

  const handleClassClick = (className) => {
    navigate("/Setup-Fee-Expenses", { state: { className } });
  };

  const handleQuickSetup = () => {
    console.log("Quick setup wizard clicked");
    // Implement quick setup wizard
  };

  const handleBulkImport = () => {
    console.log("Bulk import clicked");
    // Implement bulk import functionality
  };

  const handleExportTemplates = () => {
    console.log("Export templates clicked");
    // Implement template export
  };

  const filteredClasses = classes.filter((className) =>
    className.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "complete":
        return "setup-status-complete";
      case "partial":
        return "setup-status-partial";
      case "pending":
        return "setup-status-pending";
      default:
        return "setup-status-pending";
    }
  };

  return (
    <div className="manage-classes-screen">
      <div className="manage-classes-dashboard-wrapper">
        <Sidebar />
        <main className="manage-classes-main-content">
          {/* Header Section */}
          <div className="manage-classes-header">
            <div className="manage-classes-header-content">
              <h2 className="manage-classes-title">
                <FontAwesomeIcon
                  icon={faMoneyCheckAlt}
                  className="manage-classes-title-icon"
                />
                Financial Accounts Setup Portal
              </h2>
              <p className="manage-classes-subtitle">
                Configure fee structures, expense tracking, and financial accounts 
                for each class with our comprehensive setup wizard.
              </p>
            </div>
            
            {/* Export Templates Button */}
            <button 
              className="manage-classes-refresh-btn"
              onClick={handleExportTemplates}
            >
              <FontAwesomeIcon icon={faFileInvoiceDollar} />
              Export Templates
            </button>
          </div>

          {/* Quick Actions */}
          <div className="manage-classes-quick-actions">
            <button
              className="manage-classes-action-btn manage-classes-primary-btn"
              onClick={handleQuickSetup}
            >
              <FontAwesomeIcon icon={faPlus} />
              Quick Setup Wizard
            </button>
            <button
              className="manage-classes-action-btn"
              onClick={handleBulkImport}
            >
              <FontAwesomeIcon icon={faArrowTrendUp} />
              Bulk Import Fees
            </button>
            <button className="manage-classes-action-btn">
              <FontAwesomeIcon icon={faSortAmountDown} />
              Sort By Status
            </button>
          </div>

          {/* Search Bar */}
          <div className="manage-classes-search-container">
            <div className="manage-classes-search-icon">
              <FontAwesomeIcon icon={faSearch} />
            </div>
            <input
              type="text"
              className="manage-classes-search-input"
              placeholder="Search classes to configure accounts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Setup Statistics */}
          <div className="manage-classes-stats-overview">
            {setupStats.map((stat, index) => (
              <div 
                className="manage-classes-stat-card" 
                key={index}
                style={{ '--stat-color': stat.color }}
              >
                <div className="manage-classes-stat-content">
                  <div 
                    className="manage-classes-stat-icon"
                    style={{ 
                      background: `linear-gradient(135deg, ${stat.color} 0%, ${stat.color}88 100%)` 
                    }}
                  >
                    <FontAwesomeIcon icon={stat.icon} />
                  </div>
                  <div className="manage-classes-stat-details">
                    <div className="manage-classes-stat-value">
                      {stat.value}
                    </div>
                    <div className="manage-classes-stat-label">
                      {stat.label}
                    </div>
                  </div>
                </div>
                
                {/* Progress indicator for collection efficiency */}
                {index === 3 && (
                  <div className="setup-progress-indicator">
                    <div 
                      className="setup-progress-bar"
                      style={{ width: '92.5%' }}
                    ></div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Classes Grid Header */}
          <div className="manage-classes-grid-header">
            <FontAwesomeIcon
              icon={faCogs}
              className="manage-classes-grid-icon"
            />
            <h3 className="manage-classes-grid-title">
              Configure Class Accounts ({filteredClasses.length})
            </h3>
            <div className="setup-status-legend">
              <span className="status-legend-item complete">Configured</span>
              <span className="status-legend-item partial">Partial</span>
              <span className="status-legend-item pending">Pending</span>
            </div>
          </div>

          {/* Classes Grid */}
          <div className="manage-classes-grid-container">
            {filteredClasses.map((className, index) => (
              <div
                className="manage-classes-card setup-account-card"
                key={index}
                onClick={() => handleClassClick(className)}
              >
                {/* Status Badge */}
                <div className={`setup-status-badge ${getStatusBadgeClass(setupStatus[index].status)}`}>
                  {setupStatus[index].label}
                </div>
                
                <div className="manage-classes-card-top">
                  <img
                    src={`/Classes/${imageFilenames[index]}`}
                    alt={className}
                    className="manage-classes-image"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = `/Classes/default.png`;
                    }}
                  />
                </div>
                <div className="manage-classes-card-bottom">
                  <h3 className="manage-classes-card-title">{className}</h3>
                  <p className="manage-classes-card-grade">
                    {className.includes("Play Group") ||
                    className.includes("Nursery") ||
                    className.includes("Prep")
                      ? "Pre-School Level"
                      : `Grade ${className.replace("Class ", "")}`}
                  </p>
                  
                  {/* Setup Information */}
                  <div className="setup-info">
                    <div className="setup-info-item">
                      <span className="setup-info-label">Fee Structure:</span>
                      <span className={`setup-info-value ${setupStatus[index].status}`}>
                        {setupStatus[index].status === 'complete' ? 'Defined' : 
                         setupStatus[index].status === 'partial' ? 'Partial' : 'Not Set'}
                      </span>
                    </div>
                    <div className="setup-info-item">
                      <span className="setup-info-label">Expense Categories:</span>
                      <span className="setup-info-value">
                        {setupStatus[index].status === 'complete' ? '5' : 
                         setupStatus[index].status === 'partial' ? '2' : '0'}
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* Action Overlay */}
                <div className="manage-classes-action-overlay">
                  <button className="manage-classes-view-btn">
                    <span className="manage-classes-btn-text">
                      {setupStatus[index].status === 'complete' ? 'Manage Accounts' : 
                       setupStatus[index].status === 'partial' ? 'Complete Setup' : 'Start Setup'}
                    </span>
                    <FontAwesomeIcon
                      icon={faArrowRight}
                      className="manage-classes-btn-icon"
                    />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Setup Progress Summary */}
          <div className="setup-progress-summary">
            <div className="setup-summary-card">
              <h4 className="setup-summary-title">Setup Progress Summary</h4>
              <div className="setup-progress-overview">
                <div className="progress-item">
                  <span className="progress-label">Fully Configured</span>
                  <div className="progress-bar-container">
                    <div className="progress-bar" style={{ width: '35%' }}></div>
                  </div>
                  <span className="progress-value">5 Classes</span>
                </div>
                <div className="progress-item">
                  <span className="progress-label">Partially Setup</span>
                  <div className="progress-bar-container">
                    <div className="progress-bar partial" style={{ width: '21%' }}></div>
                  </div>
                  <span className="progress-value">3 Classes</span>
                </div>
                <div className="progress-item">
                  <span className="progress-label">Pending Setup</span>
                  <div className="progress-bar-container">
                    <div className="progress-bar pending" style={{ width: '44%' }}></div>
                  </div>
                  <span className="progress-value">6 Classes</span>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Info */}
          <div className="manage-classes-footer">
            <p className="manage-classes-footer-text">
              Click any class card to configure fee structure and expense tracking •
              <a href="#" className="manage-classes-footer-link">
                View setup guide
              </a>{" "}
              •
              <a href="#" className="manage-classes-footer-link">
                Download templates
              </a>
            </p>
            <p className="manage-classes-footer-note">
              Complete account setup enables automated billing, expense tracking, and financial reporting
            </p>
          </div>
        </main>
      </div>
    </div>
  );
};

export default SetupAccounts;